import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig'; //新增CSRF設定
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import { useLoading } from '../contexts/LoadingContext';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';

function ApiUsagePage({ darkMode }) {
  const { t } = useTranslation();
  const [apiUsageData, setApiUsageData] = useState([]);
  const { showLoading, hideLoading } = useLoading();
  
  // 分頁與排序狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('count');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    async function fetchApiUsage() {
      showLoading();
      try {
        const response = await axios.get(`${API_URL}/api/admin/api-usage`, {
          withCredentials: true,
        });
        setApiUsageData(response.data);
      } catch (err) {
        console.error(t('errorFetchingApiUsage'), err);
      } finally {
        hideLoading();
      }
    }

    fetchApiUsage();
  }, [t, showLoading, hideLoading]);

  // 處理排序
  const handleSort = (field) => {
    if (sortField === field) {
      // 相同欄位，切換排序方向
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 不同欄位，設為新欄位並默認降序
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // 排序資料
  const sortedData = [...apiUsageData].sort((a, b) => {
    if (sortField === 'endpoint') {
      return sortDirection === 'asc' 
        ? a.endpoint.localeCompare(b.endpoint) 
        : b.endpoint.localeCompare(a.endpoint);
    } else if (sortField === 'method') {
      return sortDirection === 'asc' 
        ? a.method.localeCompare(b.method) 
        : b.method.localeCompare(a.method);
    } else if (sortField === 'count') {
      return sortDirection === 'asc' 
        ? a.count - b.count 
        : b.count - a.count;
    } else if (sortField === 'lastUsedAt') {
      return sortDirection === 'asc' 
        ? new Date(a.lastUsedAt) - new Date(b.lastUsedAt) 
        : new Date(b.lastUsedAt) - new Date(a.lastUsedAt);
    }
    return 0;
  });

  // 計算分頁
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // 分頁控制
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // 渲染排序圖示
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="inline-block w-4 h-4" /> 
      : <ChevronDownIcon className="inline-block w-4 h-4" />;
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-black'}`}>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-center">{t('apiUsageStatistics')}</h1>
        
        {/* 每頁顯示數量選擇器 */}
        <div className="flex justify-end mb-4">
          <label className="mr-2 self-center">{t('itemsPerPage')}:</label>
          <select 
            value={itemsPerPage} 
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // 重置到第一頁
            }}
            className={`p-2 rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="w-full table-auto border-collapse text-sm sm:text-base">
            <thead>
              <tr className={`${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
                <th 
                  onClick={() => handleSort('endpoint')} 
                  className="border p-4 cursor-pointer hover:bg-opacity-80"
                >
                  {t('endpoint')} {renderSortIcon('endpoint')}
                </th>
                <th 
                  onClick={() => handleSort('method')} 
                  className="border p-4 cursor-pointer hover:bg-opacity-80"
                >
                  {t('method')} {renderSortIcon('method')}
                </th>
                <th 
                  onClick={() => handleSort('count')} 
                  className="border p-4 cursor-pointer hover:bg-opacity-80"
                >
                  {t('usageCount')} {renderSortIcon('count')}
                </th>
                <th 
                  onClick={() => handleSort('lastUsedAt')} 
                  className="border p-4 cursor-pointer hover:bg-opacity-80"
                >
                  {t('lastUsedAt')} {renderSortIcon('lastUsedAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((usage, index) => (
                <tr key={index} className={`${darkMode 
                  ? 'odd:bg-gray-700 even:bg-gray-600 hover:bg-gray-500' 
                  : 'odd:bg-white even:bg-gray-100 hover:bg-gray-200'} transition-colors duration-150`}
                >
                  <td className="border p-4 truncate max-w-xs break-words" title={usage.endpoint}>{usage.endpoint}</td>
                  <td className="border p-4 text-center">{usage.method}</td>
                  <td className="border p-4 text-center">{usage.count}</td>
                  <td className="border p-4">{new Date(usage.lastUsedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* 分頁控制 */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="flex">
              <button 
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-2 mx-1 rounded-md ${
                  currentPage === 1 
                    ? `${darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'} cursor-not-allowed` 
                    : `${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`
                }`}
              >
                {t('previous')}
              </button>
              
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                // 顯示當前頁和其前後的頁數
                if (
                  pageNumber === 1 || 
                  pageNumber === totalPages || 
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button 
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`px-3 py-2 mx-1 rounded-md ${
                        currentPage === pageNumber 
                          ? `${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}` 
                          : `${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 || 
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} className="px-3 py-2 mx-1">...</span>;
                }
                return null;
              })}
              
              <button 
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 mx-1 rounded-md ${
                  currentPage === totalPages 
                    ? `${darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'} cursor-not-allowed` 
                    : `${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`
                }`}
              >
                {t('next')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApiUsagePage;
